// supervisor-ticket-projection.ts
//
// Loads tickets from `<repo>/.avatar/tickets/*.json` (git-versioned, spec
// §5.1) and combines them with MissionStore's `tickets` map (runtime status,
// mission-state-reducer.ts) into scheduler.ts's `SchedulerProjection`. A
// ticket with no entry yet in the mission state defaults to "todo" (freshly
// seeded, never dispatched) — mirrors scheduler.ts's own `statusOf` default.
//
// Security audit H-1: two files with the same ticket id are refused (throw),
// never resolved by sort order — writeTicketFile removes same-id siblings, so
// a duplicate is something a worker planted to shadow a controller ticket.
import { promises as fs } from "node:fs";
import { join } from "node:path";
import type { SchedulerProjection, TicketRuntimeStatus } from "./scheduler.js";
import { type Ticket, TicketSchema } from "./schemas/ticket-schema.js";

export async function loadTicketsFromRepo(repoDir: string): Promise<Ticket[]> {
  return (await loadTicketFilesFromRepo(repoDir)).map((entry) => entry.ticket);
}

/** Every ticket with the file name it was read from (not always the one its title derives). */
export async function loadTicketFilesFromRepo(
  repoDir: string,
): Promise<{ ticket: Ticket; file: string }[]> {
  const dir = join(repoDir, ".avatar", "tickets");
  let entries: string[];
  try {
    entries = (await fs.readdir(dir)).filter((f) => f.endsWith(".json"));
  } catch {
    return [];
  }
  const tickets: { ticket: Ticket; file: string }[] = [];
  const fileOf = new Map<string, string>();
  for (const file of entries.sort()) {
    const raw = await fs.readFile(join(dir, file), "utf8");
    const ticket = TicketSchema.parse(JSON.parse(raw));
    const first = fileOf.get(ticket.id);
    if (first !== undefined) {
      throw new Error(
        `duplicate_ticket_id: ${ticket.id} có 2 file (.avatar/tickets/${first}, .avatar/tickets/${file}) — xoá file không do controller ghi rồi chạy lại`,
      );
    }
    fileOf.set(ticket.id, file);
    tickets.push({ ticket, file });
  }
  return tickets;
}

export function buildSchedulerProjection(
  tickets: Ticket[],
  missionTicketStatus: Record<string, string>,
): SchedulerProjection {
  const ticketStatus: Record<string, TicketRuntimeStatus> = {};
  for (const ticket of tickets) {
    ticketStatus[ticket.id] = (missionTicketStatus[ticket.id] as TicketRuntimeStatus) ?? "todo";
  }
  return { tickets, ticketStatus };
}
